import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { icon_search } from '@Assets';
import { CredentialSummaryList, PageWrapper, Select } from '@Components';
import { Input } from '@Components/ui-v2/input';
import { CredentialSummaryItem } from '@Models';
import { SelectOptions } from '../../Components/Select/select';

interface CredentialsSummaryProps {
  items: CredentialSummaryItem[];
}

const toOptions = (values: string[]): SelectOptions[] =>
  Array.from(new Set(values))
    .sort((a, b) => a.localeCompare(b))
    .map((value) => ({ value, label: value }));

const CredentialsSummary = ({ items }: CredentialsSummaryProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [connectorFilter, setConnectorFilter] = useState<SelectOptions | null>(null);
  const [statusFilter, setStatusFilter] = useState<SelectOptions | null>(null);

  useEffect(() => {
    setSearch('');
    setConnectorFilter(null);
    setStatusFilter(null);
  }, [items]);

  const connectorOptions = useMemo(() => toOptions(items.map((item) => item.connector)), [items]);
  const statusOptions = useMemo(() => toOptions(items.map((item) => item.status)), [items]);

  const filteredItems = items.filter((item) => {
    if (connectorFilter && item.connector !== connectorFilter.value) return false;
    if (statusFilter && item.status !== statusFilter.value) return false;
    if (!search) return true;
    const query = search.toLocaleLowerCase();
    return (
      item.providerName.toLocaleLowerCase().includes(query) || item.providerGln.toLocaleLowerCase().includes(query)
    );
  });

  return (
    <div className="h-full w-full">
      <PageWrapper title={t('CredentialsSummary.Title')}>
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <div className="flex md:flex-row mx-6 my-4 items-center flex-col gap-2">
            <span className="font-bold text-xl justify-start text-gray-700">
              {`${t('CredentialsSummary.All')} (${filteredItems.length})`}
            </span>
            <div className="md:ml-auto flex gap-2 md:flex-row flex-col w-full md:w-auto">
              <div className="w-full md:w-[180px] text-[15px]">
                <Select
                  placeholder={t('CredentialsSummary.FilterByConnector')}
                  options={connectorOptions}
                  value={connectorFilter}
                  isClearable
                  onChange={(value) => setConnectorFilter(value)}
                />
              </div>
              <div className="w-full md:w-[180px] text-[15px]">
                <Select
                  placeholder={t('CredentialsSummary.FilterByStatus')}
                  options={statusOptions}
                  value={statusFilter}
                  isClearable
                  onChange={(value) => setStatusFilter(value)}
                />
              </div>
              <div className="relative">
                <Input
                  type="text"
                  id="credentials-summary-search"
                  onChange={(e) => setSearch(e.target.value)}
                  value={search}
                  className="sm:w-40 md:w-[260px] text-[15px] pl-9"
                  placeholder={t('CredentialsSummary.Search')}
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <img src={icon_search} alt="search" className="w-4" />
                </div>
              </div>
            </div>
          </div>

          <CredentialSummaryList items={filteredItems} />
        </div>
      </PageWrapper>
    </div>
  );
};

export default CredentialsSummary;
